import { useSyncExternalStore } from "react";
import API from "../api";

const MAX_CACHED_ADMISSIONS = 50;
const ADMISSION_REFRESH_AFTER_MS = 15 * 60 * 1000;
// Signed image URLs are handed out with ≥6h of remaining lifetime (see api/src/s3.js);
// evicting under that floor guarantees a cached doc never renders expired URLs.
const ADMISSION_EVICT_AFTER_MS = 5 * 60 * 60 * 1000;
const TTL_SWEEP_INTERVAL_MS = 60 * 1000;

const docs = new Map();
const listeners = new Set();
const lastUpdatedAtById = new Map();
const fetchedAtById = new Map();
const inFlightById = new Map();

const notify = () => {
  for (const listener of listeners) listener();
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getCachedAdmission = (id) => docs.get(id);

export const useAdmission = (id) => useSyncExternalStore(subscribe, () => docs.get(id));

const evictOldestAdmissions = () => {
  while (docs.size > MAX_CACHED_ADMISSIONS) {
    const evictId = docs.keys().next().value;
    docs.delete(evictId);
    lastUpdatedAtById.delete(evictId);
    fetchedAtById.delete(evictId);
  }
};

let activeAdmissionId = null;

export const registerActiveAdmission = (id) => {
  activeAdmissionId = id;
  return () => {
    if (activeAdmissionId === id) activeAdmissionId = null;
  };
};

const isFreshAdmission = (id) => Date.now() - (fetchedAtById.get(id) || 0) < ADMISSION_REFRESH_AFTER_MS;

setInterval(() => {
  let evicted = false;
  for (const [id, fetchedAt] of fetchedAtById) {
    if (id === activeAdmissionId || Date.now() - fetchedAt < ADMISSION_EVICT_AFTER_MS) continue;
    docs.delete(id);
    lastUpdatedAtById.delete(id);
    fetchedAtById.delete(id);
    evicted = true;
  }
  if (evicted) notify();
}, TTL_SWEEP_INTERVAL_MS);

let localOverlay = null;

export const registerLocalOverlay = (overlay) => {
  localOverlay = overlay;
  return () => {
    if (localOverlay === overlay) localOverlay = null;
  };
};

export const upsertAdmission = (doc) => {
  if (!doc?._id) return;
  const incomingUpdatedAt = Date.parse(doc.updatedAt) || 0;
  if (incomingUpdatedAt && incomingUpdatedAt < (lastUpdatedAtById.get(doc._id) || 0)) return;
  if (incomingUpdatedAt) lastUpdatedAtById.set(doc._id, incomingUpdatedAt);
  fetchedAtById.set(doc._id, Date.now());

  const protectedDoc = localOverlay ? (localOverlay(doc) ?? doc) : doc;
  const merged = { ...docs.get(doc._id), ...protectedDoc };
  docs.delete(doc._id);
  docs.set(doc._id, merged);
  evictOldestAdmissions();
  notify();
};

export const writeAdmission = (sparseDoc) => {
  const existing = sparseDoc?._id && docs.get(sparseDoc._id);
  if (!existing) return;
  docs.delete(sparseDoc._id);
  docs.set(sparseDoc._id, { ...existing, ...sparseDoc });
  notify();
};

export const removeAdmission = (id) => {
  if (!docs.delete(id)) return;
  lastUpdatedAtById.delete(id);
  fetchedAtById.delete(id);
  notify();
};

let cacheEpoch = 0;

export const clearAdmissions = () => {
  cacheEpoch++;
  docs.clear();
  lastUpdatedAtById.clear();
  fetchedAtById.clear();
  inFlightById.clear();
  notify();
};

export const getAdmission = (id, options) => {
  if (inFlightById.has(id)) return inFlightById.get(id);
  const epochAtRequest = cacheEpoch;
  const request = API.get(`/admin/admission/${id}`, options)
    .then(({ ok, data }) => {
      if (!ok) throw new Error(data?.code || "Failed to load admission");
      if (epochAtRequest === cacheEpoch) upsertAdmission(data);
      return data;
    })
    .finally(() => inFlightById.delete(id));
  inFlightById.set(id, request);
  return request;
};

export const prefetchAdmission = (id) => {
  if (!id || (docs.has(id) && isFreshAdmission(id))) return;
  // priority: "low" so prefetches never compete with user-initiated requests
  // in the browser's network queue.
  getAdmission(id, { priority: "low" })
    .then((data) => {
      // Warm the browser's HTTP image cache AND decode off the main thread. `new Image()`
      // triggers the background fetch that the real <img> element reuses on render;
      // `img.decode()` runs the decode async so the user-visible paint doesn't stutter.
      const warm = (url) => {
        if (!url) return;
        const img = new Image();
        img.src = url;
        img.decode().catch(() => {});
      };
      for (const prescription of data.prescriptions || []) warm(prescription?.img);
      for (const questionRecord of data.questionRecords || []) warm(questionRecord?.documentUrl);
    })
    .catch(() => {});
};
